// 共享数据层 —— 通过 /api/* 读写服务端真实数据（SQLite），供前台对话（chat）与管理后台（admin）共用。

export interface Chapter {
  num: string;
  title: string;
  prompt: string;
}

export interface Settings {
  siteName: string;
  delay: number;
  codexModel: string;
  codexReasoningEffort: string;
}

export interface ModelReasoningEffortOption {
  reasoningEffort: string;
  description: string;
}

export interface ModelInfo {
  id: string;
  displayName: string;
  description: string;
  defaultReasoningEffort: string;
  supportedReasoningEfforts: ModelReasoningEffortOption[];
  isDefault: boolean;
}

export type LogType = "upload" | "confirm" | "revise" | "session_start" | "complete";

export interface LogEntry {
  type: LogType;
  message: string;
  session: string;
  time: string;
  timestamp: number;
}

export const DEFAULT_SETTINGS: Settings = {
  siteName: "临床方案生成助手",
  delay: 900,
  codexModel: "",
  codexReasoningEffort: "",
};

const LS_SESSION = "planai_session_id";

async function parseJson(res: Response): Promise<any> {
  return res.json().catch(() => ({}));
}

export async function getChapters(): Promise<Chapter[]> {
  const res = await fetch("/api/chapters", { credentials: "same-origin" });
  if (!res.ok) return [];
  const data = await parseJson(res);
  return data.chapters ?? [];
}

export async function saveChapters(chapters: Chapter[]): Promise<Chapter[]> {
  const res = await fetch("/api/chapters", {
    method: "PUT",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ chapters }),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.error || "保存失败");
  return data.chapters ?? chapters;
}

export async function getSettings(): Promise<Settings> {
  const res = await fetch("/api/settings", { credentials: "same-origin" });
  if (!res.ok) return DEFAULT_SETTINGS;
  const data = await parseJson(res);
  return { ...DEFAULT_SETTINGS, ...(data.settings ?? {}) };
}

export async function saveSettings(settings: Settings): Promise<Settings> {
  const res = await fetch("/api/settings", {
    method: "PUT",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(settings),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.error || "保存失败");
  return data.settings ?? settings;
}

export async function getModels(): Promise<ModelInfo[]> {
  const res = await fetch("/api/admin/models", { credentials: "same-origin" });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.error || "获取模型列表失败");
  return data.models ?? [];
}

export function getSessionId(): string {
  let id = sessionStorage.getItem(LS_SESSION);
  if (!id) {
    id = "S" + Math.floor(Math.random() * 90000 + 10000);
    sessionStorage.setItem(LS_SESSION, id);
  }
  return id;
}

export async function getLogs(): Promise<LogEntry[]> {
  const res = await fetch("/api/logs", { credentials: "same-origin" });
  if (!res.ok) return [];
  const data = await parseJson(res);
  return (data.logs ?? []).map((l: { type: LogType; message: string; session: string; timestamp: number }) => ({
    ...l,
    time: new Date(l.timestamp).toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" }),
  }));
}

export async function logEvent(type: LogType, message: string): Promise<void> {
  await fetch("/api/logs", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ type, message, session: getSessionId() }),
  });
}

export async function clearLogs(): Promise<void> {
  await fetch("/api/logs", { method: "DELETE", credentials: "same-origin" });
}
